import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { APP_NAME } from "@/lib/brand";
import { AuthBackdrop } from "@/components/layout/AuthBackdrop";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    api.get("/auth/reset-password/check", { params: { token } }).then(() => setValid(true)).catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Use at least 8 characters.");
    if (pw !== confirm) return setError("The two passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthBackdrop testid="reset-password-page">
      <div className="auth-brand serif" data-testid="reset-brand">{APP_NAME}</div>
      {valid === null && <Loader2 className="animate-spin mx-auto mt-8" />}
      {valid === false && (
        <>
          <h2 className="serif text-xl text-neutral-900 mt-6">This link has expired</h2>
          <p className="text-sm text-neutral-600 mt-3" data-testid="reset-invalid-message">Reset links work once and only for 60 minutes. Ask for a new one from the sign-in page.</p>
          <button type="button" className="btn-ink w-full mt-6" onClick={() => navigate("/login?forgot=1")} data-testid="reset-request-new-button">Send a new link</button>
        </>
      )}
      {valid && (
        <form className="flex flex-col" onSubmit={submit} data-testid="reset-password-form">
          <h2 className="serif text-xl text-neutral-900 mt-6 mb-5">Choose a new password</h2>
          <input className="login-input" type="password" placeholder="New password" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
          <input className="login-input" type="password" placeholder="Confirm new password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
          <p className="text-xs text-neutral-500 mb-4">At least 8 characters, and not your email address.</p>
          {error && <p className="text-sm text-red-600 mb-3" data-testid="reset-error">{error}</p>}
          <button className="btn-ink w-full" disabled={busy} data-testid="reset-submit-button">
            {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : "Update password"}
          </button>
        </form>
      )}
    </AuthBackdrop>
  );
}
